import React, { useEffect, useId, useRef, useState } from 'react';

/** Platform engineering palette (tailwind theme tokens). */
const PALETTE = {
  brand: '#00B4FE',
  brandLight: '#19E3F2',
  migration: '#19E3F2',
  migrationLight: '#19E3F2',
  migrationDeep: '#00123F',
  purple: '#00123F',
  purpleLight: '#19E3F2',
  purpleDeep: '#00123F',
  ink: '#00123F',
  muted: '#64748b',
};

const BRAND = PALETTE.brand;
const CYAN = PALETTE.brandLight;
const MIGRATION = PALETTE.migration;
const PURPLE = PALETTE.purple;
const INK = PALETTE.ink;
const MUTED = PALETTE.muted;

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

function useReplayOnInView(threshold = 0.2) {
  const ref = useRef(null);
  const [playKey, setPlayKey] = useState(0);
  const inView = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !inView.current) {
          inView.current = true;
          setPlayKey((k) => k + 1);
        } else if (!entry.isIntersecting && inView.current) {
          inView.current = false;
        }
      },
      { threshold, rootMargin: '0px 0px -5% 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, playKey, active: playKey > 0 };
}

function VisualFrame({ caption, captionDelay = '2.8s', frameRef, playKey, active, minHeight = 360, children }) {
  return (
    <div ref={frameRef} className="ia-diagram w-full min-w-0">
      {active ? (
        <div key={playKey} className="w-full">
          {children}
          {caption ? (
            <p className="ia-caption ia-caption-text mx-auto mt-6 max-w-lg text-center" style={{ animationDelay: captionDelay }}>
              {caption}
            </p>
          ) : null}
        </div>
      ) : (
        <div style={{ minHeight }} aria-hidden="true" />
      )}
    </div>
  );
}

/* compact satellite glyphs (16×16) */
function createGlyphs(stroke) {
  return {
    metrics: () => <path d="M2 12 L6 7 L9 9 L14 3" fill="none" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />,
    doc: () => (
      <>
        <rect x="3.5" y="2.5" width="9" height="11" rx="1.5" fill="none" stroke={stroke} strokeWidth="1.4" />
        <line x1="6" y1="6.5" x2="10" y2="6.5" stroke={stroke} strokeWidth="1.2" />
        <line x1="6" y1="9.5" x2="9" y2="9.5" stroke={stroke} strokeWidth="1.2" />
      </>
    ),
    people: () => (
      <>
        <circle cx="8" cy="5.5" r="2.4" fill="none" stroke={stroke} strokeWidth="1.4" />
        <path d="M3.5 13.5c0-2.6 2-4 4.5-4s4.5 1.4 4.5 4" fill="none" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" />
      </>
    ),
    chat: () => (
      <>
        <rect x="2.5" y="3.5" width="11" height="7.5" rx="2" fill="none" stroke={stroke} strokeWidth="1.4" />
        <path d="M5.5 13.5 L7.5 11" fill="none" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" />
      </>
    ),
    cursor: () => <path d="M4 3 L13 8.5 L8.5 9.5 L7 14 Z" fill="none" stroke={stroke} strokeWidth="1.4" strokeLinejoin="round" />,
    spark: () => (
      <path d="M8 2l1 3.6L12.6 7 9 7.6 8 12l-1-4.4L3.4 7 7 6.4 8 2z" fill="none" stroke={stroke} strokeWidth="1.35" strokeLinejoin="round" />
    ),
    Analyst: () => (
      <>
        <circle cx="8" cy="5.5" r="2.8" fill="none" stroke={stroke} strokeWidth="1.5" />
        <path d="M3 14c0-2.9 2.2-4.4 5-4.4s5 1.5 5 4.4" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" />
      </>
    ),
  };
}

const GlyphBusiness = createGlyphs(MIGRATION);
const GlyphAgent = createGlyphs(PURPLE);
const Glyph = GlyphBusiness;

function Satellite({ cx, cy, glyph, ringColor = `${BRAND}55`, delay = '0s', pop = true, r = 15 }) {
  const G = glyph;
  const iconScale = r <= 14 ? 0.82 : 0.9;

  return (
    <g
      className={pop ? 'ia-pop' : undefined}
      style={pop ? { animationDelay: delay } : undefined}
      transform={`translate(${cx}, ${cy})`}
    >
      <circle r={r} fill="#ffffff" stroke={ringColor} strokeWidth="1.25" />
      <g transform={`scale(${iconScale}) translate(-8, -8)`}>
        <G />
      </g>
    </g>
  );
}

function resolveCircleWall(particle, maxDist) {
  const dist = Math.hypot(particle.x, particle.y);
  if (dist <= maxDist || dist === 0) return;

  const nx = particle.x / dist;
  const ny = particle.y / dist;
  particle.x = nx * maxDist;
  particle.y = ny * maxDist;

  const normalSpeed = particle.vx * nx + particle.vy * ny;
  if (normalSpeed > 0) {
    particle.vx -= 2 * normalSpeed * nx;
    particle.vy -= 2 * normalSpeed * ny;
  }
}

function resolvePairCollision(a, b, minDist) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dist = Math.hypot(dx, dy);
  if (dist >= minDist || dist === 0) return;

  const nx = dx / dist;
  const ny = dy / dist;
  const overlap = minDist - dist;
  a.x -= nx * overlap * 0.5;
  a.y -= ny * overlap * 0.5;
  b.x += nx * overlap * 0.5;
  b.y += ny * overlap * 0.5;

  const relVx = b.vx - a.vx;
  const relVy = b.vy - a.vy;
  const closing = relVx * nx + relVy * ny;
  if (closing >= 0) return;

  const impulse = -closing * 0.5;
  a.vx -= impulse * nx;
  a.vy -= impulse * ny;
  b.vx += impulse * nx;
  b.vy += impulse * ny;
}

function BouncingSatellites({
  centerX,
  centerY,
  boundsR,
  satelliteR,
  bouncers,
  clipPathId,
  active,
  playKey,
  reducedMotion,
  ringColor = `${BRAND}55`,
  startDelay = 0,
}) {
  const particlesRef = useRef(bouncers.map((b) => ({ ...b })));
  const [, setFrame] = useState(0);

  useEffect(() => {
    particlesRef.current = bouncers.map((b) => ({ ...b }));
  }, [playKey, bouncers]);

  useEffect(() => {
    if (!active || reducedMotion) return undefined;

    const maxDist = boundsR - satelliteR;
    const minPairDist = satelliteR * 2 + 3;
    let rafId;
    let last = performance.now();
    let timeoutId;

    const tick = (now) => {
      const dt = Math.min((now - last) / 1000, 0.033);
      last = now;
      const pts = particlesRef.current;

      for (const p of pts) {
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        resolveCircleWall(p, maxDist);
      }

      for (let i = 0; i < pts.length; i += 1) {
        for (let j = i + 1; j < pts.length; j += 1) {
          resolvePairCollision(pts[i], pts[j], minPairDist);
        }
        resolveCircleWall(pts[i], maxDist);
      }

      setFrame((f) => f + 1);
      rafId = requestAnimationFrame(tick);
    };

    timeoutId = setTimeout(() => {
      last = performance.now();
      rafId = requestAnimationFrame(tick);
    }, startDelay);

    return () => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(rafId);
    };
  }, [active, playKey, reducedMotion, boundsR, satelliteR, startDelay]);

  const particles = particlesRef.current;

  return (
    <g clipPath={clipPathId ? `url(#${clipPathId})` : undefined}>
      <g transform={`translate(${centerX}, ${centerY})`}>
        {particles.map((p) => (
          <Satellite key={p.id} cx={p.x} cy={p.y} glyph={p.glyph} ringColor={ringColor} pop={false} r={satelliteR} />
        ))}
      </g>
    </g>
  );
}

/** Layered semantic-model hub: three governed slabs with label on top. */
function SemanticLayerHub({ cx, cy, uid, w = 132, slabH = 34, gap = 7 }) {
  const x = cx - w / 2;
  const totalH = slabH * 3 + gap * 2;
  const y0 = cy - totalH / 2;
  const y1 = y0 + slabH + gap;
  const y2 = y1 + slabH + gap;

  return (
    <>
      <ellipse cx={cx} cy={cy + 12} rx={w * 0.44} ry={slabH * 0.52} fill={BRAND} opacity="0.1" />

      <g opacity="0.55">
        <rect x={x + 10} y={y0} width={w - 20} height={slabH} rx="9" fill="#ffffff" stroke={`${MIGRATION}44`} strokeWidth="1.35" />
        <line x1={x + 24} y1={y0 + slabH / 2 - 2} x2={x + w - 24} y2={y0 + slabH / 2 - 2} stroke={MIGRATION} strokeWidth="2" strokeLinecap="round" opacity="0.35" />
        <line x1={x + 24} y1={y0 + slabH / 2 + 7} x2={x + w - 36} y2={y0 + slabH / 2 + 7} stroke={MIGRATION} strokeWidth="2" strokeLinecap="round" opacity="0.25" />
      </g>

      <g opacity="0.78">
        <rect x={x + 5} y={y1} width={w - 10} height={slabH} rx="10" fill="#ffffff" stroke={`${BRAND}44`} strokeWidth="1.35" />
        <line x1={x + 20} y1={y1 + slabH / 2 - 2} x2={x + w - 20} y2={y1 + slabH / 2 - 2} stroke={BRAND} strokeWidth="2" strokeLinecap="round" opacity="0.4" />
        <line x1={x + 20} y1={y1 + slabH / 2 + 7} x2={x + w - 32} y2={y1 + slabH / 2 + 7} stroke={BRAND} strokeWidth="2" strokeLinecap="round" opacity="0.3" />
      </g>

      <g filter={`url(#${uid}-hub-sh)`}>
        <rect x={x} y={y2} width={w} height={slabH + 12} rx="12" fill={`url(#${uid}-hex)`} stroke="#ffffff" strokeWidth="2" strokeOpacity="0.8" />
        <rect x={x + 2} y={y2 + 2} width={w - 4} height="13" rx="9" fill="#ffffff" opacity="0.2" />
        <text x={cx} y={y2 + slabH + 1} textAnchor="middle" fill="#ffffff" fontSize="15" fontWeight="800" letterSpacing="0.03em">
          Semantic Layer
        </text>
      </g>
    </>
  );
}

const LEFT_BOUNCERS = [
  { id: 'metrics', x: -28, y: -38, vx: 34, vy: 26, glyph: GlyphBusiness.metrics },
  { id: 'doc', x: 32, y: -18, vx: -30, vy: 32, glyph: GlyphBusiness.doc },
  { id: 'people', x: -8, y: 36, vx: 26, vy: -28, glyph: GlyphBusiness.people },
];

const RIGHT_BOUNCERS = [
  { id: 'chat', x: -26, y: -34, vx: 32, vy: 28, glyph: GlyphAgent.chat },
  { id: 'cursor', x: 30, y: -22, vx: -28, vy: 30, glyph: GlyphAgent.cursor },
  { id: 'spark', x: -4, y: 34, vx: 24, vy: -32, glyph: GlyphAgent.spark },
];

export function SemanticControlPlaneVisual() {
  const reducedMotion = usePrefersReducedMotion();
  const uid = useId().replace(/:/g, '');
  const W = 820;
  const H = 400;
  const { ref, playKey, active } = useReplayOnInView();

  const lcx = 158;
  const rcx = 662;
  const cyc = 200;
  const rSphere = 92;
  const rHalo = 130;
  const rRing = 120;
  const hexCx = W / 2;
  const hubW = 132;
  const hubLeft = hexCx - hubW / 2;
  const hubRight = hexCx + hubW / 2;

  const rSatellite = 14;
  const bounceDelay = reducedMotion ? 0 : 1250;

  const leftPath = `M ${lcx + rSphere} ${cyc} Q ${hexCx - 42} ${cyc - 12} ${hubLeft} ${cyc}`;
  const rightPath = `M ${rcx - rSphere} ${cyc} Q ${hexCx + 42} ${cyc - 12} ${hubRight} ${cyc}`;

  return (
    <VisualFrame
      frameRef={ref}
      playKey={playKey}
      active={active}
      caption="One semantic foundation. Many trusted surfaces."
      captionDelay="3.4s"
      minHeight={400}
    >
      <svg viewBox={`0 0 ${W} ${H}`} className="mx-auto h-auto w-full overflow-visible" aria-hidden="true">
        <defs>
          <radialGradient id={`${uid}-sphereA`} cx="38%" cy="34%" r="72%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="45%" stopColor={PALETTE.migrationLight} stopOpacity="0.55" />
            <stop offset="100%" stopColor={MIGRATION} stopOpacity="0.82" />
          </radialGradient>
          <radialGradient id={`${uid}-sphereB`} cx="62%" cy="34%" r="72%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="45%" stopColor={PALETTE.purpleLight} stopOpacity="0.55" />
            <stop offset="100%" stopColor={PURPLE} stopOpacity="0.78" />
          </radialGradient>
          <radialGradient id={`${uid}-haloA`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={PALETTE.migrationLight} stopOpacity="0.28" />
            <stop offset="100%" stopColor={MIGRATION} stopOpacity="0" />
          </radialGradient>
          <radialGradient id={`${uid}-haloB`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={PALETTE.purpleLight} stopOpacity="0.28" />
            <stop offset="100%" stopColor={PURPLE} stopOpacity="0" />
          </radialGradient>
          <linearGradient id={`${uid}-beam-left`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={MIGRATION} />
            <stop offset="100%" stopColor={BRAND} />
          </linearGradient>
          <linearGradient id={`${uid}-beam-right`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={BRAND} />
            <stop offset="100%" stopColor={PURPLE} />
          </linearGradient>
          <linearGradient id={`${uid}-hex`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={CYAN} stopOpacity="0.95" />
            <stop offset="100%" stopColor={BRAND} />
          </linearGradient>
          <filter id={`${uid}-hub-sh`} x="-20%" y="-20%" width="140%" height="160%">
            <feDropShadow dx="0" dy="8" stdDeviation="10" floodColor={INK} floodOpacity="0.14" />
          </filter>
          <clipPath id={`${uid}-clip-left`}>
            <circle cx={lcx} cy={cyc} r={rSphere - 1} />
          </clipPath>
          <clipPath id={`${uid}-clip-right`}>
            <circle cx={rcx} cy={cyc} r={rSphere - 1} />
          </clipPath>
        </defs>

        {/* Context gap: visible while orbs are apart */}
        <g className="ia-gap-pulse" style={{ animationDelay: '0.25s' }}>
          <line x1={W / 2} y1={cyc - 72} x2={W / 2} y2={cyc + 72} stroke={MUTED} strokeWidth="2.5" strokeDasharray="4 8" strokeLinecap="round" opacity="0.7" />
          <text x={W / 2} y={cyc - 88} textAnchor="middle" fill={MUTED} fontSize="14" fontWeight="700" letterSpacing="0.16em">
            CONTEXT GAP
          </text>
        </g>

        {/* Semantic layer hub: appears first at center */}
        <g className="ia-scale-in" style={{ animationDelay: '0.05s' }}>
          <SemanticLayerHub cx={hexCx} cy={cyc} uid={uid} w={hubW} />
        </g>

        {/* Connector beams: draw in after orbs converge */}
        <g className="ia-connect-draw">
          <path
            d={leftPath}
            className="ia-connect-path"
            fill="none"
            stroke={`url(#${uid}-beam-left)`}
            strokeWidth="3.5"
            strokeLinecap="round"
            pathLength="100"
          />
          <path
            d={rightPath}
            className="ia-connect-path ia-connect-path-delay"
            fill="none"
            stroke={`url(#${uid}-beam-right)`}
            strokeWidth="3.5"
            strokeLinecap="round"
            pathLength="100"
          />
        </g>

        {/* Left cluster: slides in from farther left */}
        <g className="ia-converge-left" style={{ animationDelay: '0.15s' }}>
          <circle cx={lcx} cy={cyc} r={rHalo} fill={`url(#${uid}-haloA)`} />
          <g transform={`translate(${lcx}, ${cyc})`}>
            <circle r={rRing} fill="none" stroke={`${MIGRATION}33`} strokeWidth="1.25" strokeDasharray="2 7" />
          </g>
          <circle cx={lcx} cy={cyc} r={rSphere} fill={`url(#${uid}-sphereA)`} stroke={`${MIGRATION}55`} strokeWidth="1.25" />
          <BouncingSatellites
            centerX={lcx}
            centerY={cyc}
            boundsR={rSphere}
            satelliteR={rSatellite}
            bouncers={LEFT_BOUNCERS}
            clipPathId={`${uid}-clip-left`}
            active={active}
            playKey={playKey}
            reducedMotion={reducedMotion}
            ringColor={`${MIGRATION}66`}
            startDelay={bounceDelay}
          />
          <text x={lcx} y={cyc + 8} textAnchor="middle" fill={INK} fontSize="21" fontWeight="800" letterSpacing="-0.01em">
            Business
          </text>
          <text x={lcx} y={cyc + 32} textAnchor="middle" fill={INK} fontSize="21" fontWeight="800" letterSpacing="-0.01em">
            Context
          </text>
        </g>

        {/* Right cluster: slides in from farther right */}
        <g className="ia-converge-right" style={{ animationDelay: '0.15s' }}>
          <circle cx={rcx} cy={cyc} r={rHalo} fill={`url(#${uid}-haloB)`} />
          <g transform={`translate(${rcx}, ${cyc})`}>
            <circle r={rRing} fill="none" stroke={`${PURPLE}33`} strokeWidth="1.25" strokeDasharray="2 7" />
          </g>
          <circle cx={rcx} cy={cyc} r={rSphere} fill={`url(#${uid}-sphereB)`} stroke={`${PURPLE}55`} strokeWidth="1.25" />
          <BouncingSatellites
            centerX={rcx}
            centerY={cyc}
            boundsR={rSphere}
            satelliteR={rSatellite}
            bouncers={RIGHT_BOUNCERS}
            clipPathId={`${uid}-clip-right`}
            active={active}
            playKey={playKey}
            reducedMotion={reducedMotion}
            ringColor={`${PURPLE}66`}
            startDelay={bounceDelay}
          />
          <text x={rcx} y={cyc + 10} textAnchor="middle" fill={INK} fontSize="21" fontWeight="800" letterSpacing="-0.01em">
            AI Agents
          </text>
        </g>
      </svg>
    </VisualFrame>
  );
}

const GOLD = '#F59E0B';
const GOLD_LIGHT = '#FDE68A';
const GOLD_DEEP = '#B45309';

function GoldShieldMark({ uid }) {
  return (
    <g transform="scale(0.7)">
      <circle r="20" fill={GOLD} opacity="0.14" />
      <circle r="14" fill={`url(#${uid}-gold)`} stroke={GOLD_DEEP} strokeWidth="1.25" opacity="0.95" />
      <path
        d="M0 -8 L5.5 -5.2 V1.2 C5.5 5.2 3 7.2 0 8 C-3 7.2 -5.5 5.2 -5.5 1.2 V-5.2 Z"
        fill="#ffffff"
        fillOpacity="0.92"
      />
      <path d="M-2.5 1.5 L-0.5 3.5 L3 -2" fill="none" stroke={GOLD_DEEP} strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  );
}

function IconDatabase({ color }) {
  return (
    <g fill="none" stroke={color} strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="8" cy="4.5" rx="5.2" ry="1.9" />
      <path d="M2.8 4.5v4.2c0 1.1 2.3 2 5.2 2s5.2-.9 5.2-2V4.5" />
    </g>
  );
}

function IconCog({ color }) {
  return (
    <g fill="none" stroke={color} strokeWidth="1.3" strokeLinecap="round">
      <circle cx="8" cy="8" r="2.2" />
      <circle cx="8" cy="8" r="4.8" />
      <path d="M8 2.6v1.4M8 11v1.4M2.6 8h1.4M11 8h1.4" />
    </g>
  );
}

function IconDocument({ color }) {
  return (
    <g fill="none" stroke={color} strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 2.5h5l2.2 2.2v7.8H4.5V2.5z" />
      <path d="M9.5 2.5v2.2h2.2" />
      <line x1="6" y1="8" x2="10.5" y2="8" />
    </g>
  );
}

function IsometricNode({ x, y, label, accent, faceId, Icon, delay }) {
  const w = 74;
  const half = w / 2;

  return (
    <g transform={`translate(${x}, ${y})`}>
      <g className="ia-pop" style={{ animationDelay: delay }}>
        <ellipse cx="5" cy="34" rx="40" ry="9" fill={INK} opacity="0.06" />
        <path
          d={`M ${-half + 8} 8 L ${half + 8} 8 L ${half + 2} 20 L ${-half + 2} 20 Z`}
          fill={accent}
          opacity="0.14"
        />
        <path
          d={`M ${-half} -6 L ${half} -6 L ${half + 8} 8 L ${-half + 8} 8 Z`}
          fill={`url(#${faceId})`}
          stroke={accent}
          strokeWidth="1.1"
          strokeOpacity="0.35"
        />
        <path
          d={`M ${-half + 8} 8 L ${half + 8} 8`}
          fill="none"
          stroke={accent}
          strokeWidth="1"
          strokeOpacity="0.2"
        />
        <svg x="-13" y="-19" width="26" height="26" viewBox="0 0 16 16" aria-hidden="true">
          <Icon color={accent} />
        </svg>
        <text y="52" textAnchor="middle" fill={INK} fontSize="13" fontWeight="700" letterSpacing="-0.01em" opacity="0.88">
          {label}
        </text>
      </g>
    </g>
  );
}

/* ════════════════════════════════════════════════════════
   DIAGRAM 2: Governed Access (triangle icons + traveling shield)
   ════════════════════════════════════════════════════════ */
export function AccessEvaluationVisual() {
  const uid = useId().replace(/:/g, '');
  const reducedMotion = usePrefersReducedMotion();
  const { ref, playKey, active } = useReplayOnInView();

  const W = 820;
  const H = 400;
  const cx = W / 2;
  const spread = 195;

  const nodes = {
    semantic: { key: 'semantic', x: cx, y: 108, label: 'Semantic Layer', accent: BRAND, fill: '#f5fbff', Icon: IconCog, delay: '0.35s' },
    data: { key: 'data', x: cx - spread, y: 292, label: 'Data Layer', accent: MIGRATION, fill: '#f4fdfa', Icon: IconDatabase, delay: '0.1s' },
    reporting: { key: 'reporting', x: cx + spread, y: 292, label: 'Reporting Layer', accent: PURPLE, fill: '#faf8ff', Icon: IconDocument, delay: '0.2s' },
  };

  const { data, semantic, reporting } = nodes;
  const triangleFill = `M ${data.x} ${data.y + 8} L ${semantic.x} ${semantic.y + 8} L ${reporting.x} ${reporting.y + 8} Z`;
  const edge = (a, b) => `M ${a.x} ${a.y + 2} L ${b.x} ${b.y + 2}`;

  return (
    <VisualFrame
      frameRef={ref}
      playKey={playKey}
      active={active}
      caption="Same user. Same scope. Same answer."
      captionDelay="3.2s"
      minHeight={400}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="mx-auto block h-auto w-full max-w-[820px] overflow-visible"
        style={{
          '--gov-data-x': `${nodes.data.x}px`,
          '--gov-data-y': `${nodes.data.y}px`,
          '--gov-semantic-x': `${nodes.semantic.x}px`,
          '--gov-semantic-y': `${nodes.semantic.y}px`,
          '--gov-reporting-x': `${nodes.reporting.x}px`,
          '--gov-reporting-y': `${nodes.reporting.y}px`,
        }}
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={`${uid}-gold`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={GOLD_LIGHT} />
            <stop offset="100%" stopColor={GOLD} />
          </linearGradient>
          <linearGradient id={`${uid}-face-data`} x1="0%" y1="0%" x2="20%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor={nodes.data.fill} />
          </linearGradient>
          <linearGradient id={`${uid}-face-semantic`} x1="0%" y1="0%" x2="20%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor={nodes.semantic.fill} />
          </linearGradient>
          <linearGradient id={`${uid}-face-reporting`} x1="0%" y1="0%" x2="20%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor={nodes.reporting.fill} />
          </linearGradient>
        </defs>

        <path className="ia-fade-up" style={{ animationDelay: '0.45s' }} d={triangleFill} fill={BRAND} fillOpacity="0.04" stroke="none" />
        <g className="ia-fade-up" style={{ animationDelay: '0.5s' }} stroke={BRAND} strokeOpacity="0.16" strokeWidth="1.25" fill="none">
          <path d={edge(data, semantic)} />
          <path d={edge(semantic, reporting)} />
          <path d={edge(reporting, data)} />
        </g>

        <IsometricNode {...nodes.data} faceId={`${uid}-face-data`} />
        <IsometricNode {...nodes.reporting} faceId={`${uid}-face-reporting`} />
        <IsometricNode {...nodes.semantic} faceId={`${uid}-face-semantic`} />

        {/* single shield: visits each node */}
        {reducedMotion ? (
          <g transform={`translate(${nodes.semantic.x}, ${nodes.semantic.y})`}>
            <GoldShieldMark uid={uid} />
          </g>
        ) : (
          <g className="ia-govern-shield-travel">
            <GoldShieldMark uid={uid} />
          </g>
        )}

        <text className="ia-fade-up" style={{ animationDelay: '1.1s' }} x={cx} y={368} textAnchor="middle" fill={MUTED} fontSize="11" fontWeight="700" letterSpacing="0.16em" opacity="0.75">
          GOVERNED ACCESS
        </text>
      </svg>
    </VisualFrame>
  );
}

/* ════════════════════════════════════════════════════════
   DIAGRAM 3: Integrated Command Hub (orbital command center)
   ════════════════════════════════════════════════════════ */
export function CommandHubDashboardVisual() {
  const uid = useId().replace(/:/g, '');
  const reducedMotion = usePrefersReducedMotion();
  const { ref, playKey, active } = useReplayOnInView();

  const W = 820;
  const H = 470;
  const cx = 410;
  const cy = 235;
  const hubR = 92;
  const ringOuter = 192;

  const dots = [0, 1, 2, 3, 4].map((k) => {
    const theta = (k * 72 * Math.PI) / 180;
    return { x: cx + ringOuter * Math.sin(theta), y: cy - ringOuter * Math.cos(theta) };
  });

  const edgePoint = (px, py) => {
    const dx = cx - px;
    const dy = cy - py;
    const dist = Math.hypot(dx, dy);
    return { x: cx - (dx / dist) * (hubR + 6), y: cy - (dy / dist) * (hubR + 6) };
  };

  const chips = [
    { label: 'Query History', x: 152, y: 92, delay: '1.9s' },
    { label: 'Metric Usage', x: 668, y: 92, delay: '2.02s' },
    { label: 'Governance Signals', x: cx, y: 442, delay: '2.14s' },
  ];

  return (
    <VisualFrame
      frameRef={ref}
      playKey={playKey}
      active={active}
      caption="One command layer for visibility, context, and governed analytics activity."
      captionDelay="2.9s"
      minHeight={430}
    >
      <svg viewBox={`0 0 ${W} ${H}`} className="mx-auto h-auto w-full overflow-visible" aria-hidden="true">
        <defs>
          <radialGradient id={`${uid}-core`} cx="42%" cy="34%" r="72%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="55%" stopColor={CYAN} stopOpacity="0.4" />
            <stop offset="100%" stopColor={BRAND} stopOpacity="0.85" />
          </radialGradient>
          <radialGradient id={`${uid}-corehalo`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={CYAN} stopOpacity="0.3" />
            <stop offset="100%" stopColor={BRAND} stopOpacity="0" />
          </radialGradient>
          <filter id={`${uid}-sh`} x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="0" dy="12" stdDeviation="16" floodColor={INK} floodOpacity="0.12" />
          </filter>
        </defs>

        {/* orbit rings */}
        <circle className="ia-fade-up" style={{ animationDelay: '0.4s' }} cx={cx} cy={cy} r="150" fill="none" stroke={`${BRAND}26`} strokeWidth="1.25" strokeDasharray="2 8" />
        <circle className="ia-fade-up" style={{ animationDelay: '0.5s' }} cx={cx} cy={cy} r={ringOuter} fill="none" stroke={`${BRAND}1f`} strokeWidth="1.25" strokeDasharray="2 8" />

        {/* connectors dot -> hub */}
        {dots.map((d, i) => {
          const e = edgePoint(d.x, d.y);
          const mx = (d.x + e.x) / 2 + (d.y - cy) * 0.08;
          const my = (d.y + e.y) / 2 + (cx - d.x) * 0.08;
          return (
            <path
              key={`arc-${i}`}
              className="ia-draw"
              style={{ animationDelay: `${0.9 + i * 0.08}s` }}
              d={`M ${d.x} ${d.y} Q ${mx} ${my} ${e.x} ${e.y}`}
              fill="none"
              stroke={`${BRAND}66`}
              strokeWidth="1.5"
              strokeLinecap="round"
              pathLength="100"
              strokeDasharray="100"
            />
          );
        })}

        {/* query pulses */}
        {!reducedMotion
          ? dots.map((d, i) => {
              const e = edgePoint(d.x, d.y);
              const mx = (d.x + e.x) / 2 + (d.y - cy) * 0.08;
              const my = (d.y + e.y) / 2 + (cx - d.x) * 0.08;
              const path = `M ${d.x} ${d.y} Q ${mx} ${my} ${e.x} ${e.y}`;
              return (
                <circle key={`pulse-${i}`} r="4.5" fill={CYAN} opacity="0">
                  <animateMotion dur="1.2s" begin={`${1.5 + i * 0.16}s`} fill="freeze" path={path} />
                  <animate attributeName="opacity" values="0;0.9;0.9;0" keyTimes="0;0.18;0.78;1" dur="1.2s" begin={`${1.5 + i * 0.16}s`} fill="freeze" />
                </circle>
              );
            })
          : null}

        {/* user/query dots */}
        {dots.map((d, i) => (
          <g key={`dot-${i}`} className="ia-pop" style={{ animationDelay: `${0.6 + i * 0.1}s` }}>
            <circle cx={d.x} cy={d.y} r="16" fill="#ffffff" stroke={`${BRAND}55`} strokeWidth="1.25" />
            <circle cx={d.x} cy={d.y} r="6.5" fill={BRAND} opacity="0.85" />
          </g>
        ))}

        {/* hub core */}
        <g className="ia-pop" style={{ animationDelay: '0.2s' }}>
          <circle cx={cx} cy={cy} r="132" fill={`url(#${uid}-corehalo)`} />
          <g filter={`url(#${uid}-sh)`}>
            <circle cx={cx} cy={cy} r={hubR} fill={`url(#${uid}-core)`} stroke={BRAND} strokeWidth="2" />
            <circle cx={cx} cy={cy} r={hubR - 9} fill="none" stroke="#ffffff" strokeWidth="1.25" opacity="0.6" />
          </g>
          <g transform={`translate(${cx - 8}, ${cy - 52})`}>
            <Glyph.Analyst />
          </g>
          <text x={cx} y={cy - 8} textAnchor="middle" fill={INK} fontSize="18" fontWeight="800" letterSpacing="-0.01em">
            Command Hub
          </text>
          <g className="ia-fade-up" style={{ animationDelay: '1.6s' }}>
            <rect x={cx - 58} y={cy + 8} width="116" height="26" rx="13" fill={`${PURPLE}12`} stroke={`${PURPLE}80`} strokeWidth="1" />
            <text x={cx} y={cy + 25} textAnchor="middle" fill={PURPLE} fontSize="11.5" fontWeight="800" letterSpacing="0.03em">
              Semantic Layer
            </text>
          </g>
        </g>

        {/* visibility chips */}
        {chips.map((c) => {
          const w = c.label.length * 7.2 + 36;
          return (
            <g key={c.label} className="ia-fade-up" style={{ animationDelay: c.delay }}>
              <rect x={c.x - w / 2} y={c.y - 17} width={w} height="34" rx="17" fill="#ffffff" stroke={`${BRAND}3d`} strokeWidth="1.25" />
              <circle cx={c.x - w / 2 + 16} cy={c.y} r="3.5" fill={CYAN} />
              <text x={c.x - w / 2 + 28} y={c.y + 4} fill={INK} fontSize="12.5" fontWeight="700">
                {c.label}
              </text>
            </g>
          );
        })}
      </svg>
    </VisualFrame>
  );
}
